import { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { adminEmails, auth, db, isFirebaseConfigured } from "../firebase";

const AuthContext = createContext(null);
const HARDCODED_ADMIN = {
  email: "admin@seekersbay.com",
  password: "Admin@123",
  name: "Seekersbay Admin",
};

function isOfflineFirestoreError(error) {
  return (
    error?.code === "unavailable" ||
    error?.message?.toLowerCase().includes("client is offline")
  );
}

function getStoredMockSession() {
  const raw = localStorage.getItem("mockUser") || sessionStorage.getItem("mockUser");
  return raw ? JSON.parse(raw) : null;
}

function storeMockSession(user, rememberMe = true) {
  const storageTarget = rememberMe ? localStorage : sessionStorage;
  const alternateStorage = rememberMe ? sessionStorage : localStorage;
  storageTarget.setItem("mockUser", JSON.stringify(user));
  alternateStorage.removeItem("mockUser");
}

function clearMockSession() {
  localStorage.removeItem("mockUser");
  sessionStorage.removeItem("mockUser");
}

function isHardcodedAdmin(email, password) {
  return (
    email?.trim().toLowerCase() === HARDCODED_ADMIN.email.toLowerCase() &&
    password === HARDCODED_ADMIN.password
  );
}

async function recordLoginEvent(profile) {
  if (!db) {
    return;
  }

  await setDoc(
    doc(db, "loginEvents", `${profile.uid}-${Date.now()}`),
    {
      uid: profile.uid,
      email: profile.email,
      name: profile.name || "",
      role: profile.role || "user",
      loggedInAt: serverTimestamp(),
    }
  );
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const mockUser = getStoredMockSession();

    if (mockUser?.uid === "hardcoded-admin" && mockUser?.role === "admin") {
      setCurrentUser(mockUser);
      setLoading(false);
      return undefined;
    }

    if (!isFirebaseConfigured || !auth || !db) {
      setCurrentUser(mockUser);
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const storedMockUser = getStoredMockSession();

      if (storedMockUser?.uid === "hardcoded-admin" && storedMockUser?.role === "admin") {
        setCurrentUser(storedMockUser);
        setLoading(false);
        return;
      }

      if (!firebaseUser) {
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      try {
        const snapshot = await getDoc(doc(db, "users", firebaseUser.uid));
        const profileData = snapshot.exists() ? snapshot.data() : {};
        setCurrentUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName || profileData.name || "",
          phone: profileData.phone || "",
          role: profileData.role || "user",
        });
      } catch (error) {
        if (!isOfflineFirestoreError(error)) {
          throw error;
        }

        setCurrentUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "",
          phone: "",
          role: "user",
        });
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signup = async ({ name, email, phone, password, rememberMe }) => {
    if (email?.trim().toLowerCase() === HARDCODED_ADMIN.email.toLowerCase()) {
      throw new Error("This email is reserved for admin login only.");
    }

    if (!isFirebaseConfigured || !auth || !db) {
      const mockUser = {
        uid: `mock-${Date.now()}`,
        name,
        email,
        phone,
        role: adminEmails.includes(email.toLowerCase()) ? "admin" : "user",
      };
      storeMockSession(mockUser, rememberMe);
      const users = JSON.parse(localStorage.getItem("users") || "[]");
      localStorage.setItem(
        "users",
        JSON.stringify([{ ...mockUser, createdAt: new Date().toISOString() }, ...users])
      );
      setCurrentUser(mockUser);
      return mockUser;
    }

    const credential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(credential.user, { displayName: name });
    const role = adminEmails.includes(email.toLowerCase()) ? "admin" : "user";

    await setDoc(doc(db, "users", credential.user.uid), {
      uid: credential.user.uid,
      name,
      email,
      phone,
      role,
      createdAt: serverTimestamp(),
    });

    const profile = { uid: credential.user.uid, name, email, phone, role };
    setCurrentUser(profile);
    await recordLoginEvent(profile);
    return profile;
  };

  const login = async ({ email, password, rememberMe }) => {
    if (isHardcodedAdmin(email, password)) {
      if (isFirebaseConfigured && auth?.currentUser) {
        await signOut(auth);
      }

      const adminUser = {
        uid: "hardcoded-admin",
        name: HARDCODED_ADMIN.name,
        email: HARDCODED_ADMIN.email,
        phone: "",
        role: "admin",
      };

      storeMockSession(adminUser, rememberMe);
      const logins = JSON.parse(localStorage.getItem("loginEvents") || "[]");
      localStorage.setItem(
        "loginEvents",
        JSON.stringify([{ ...adminUser, loggedInAt: new Date().toISOString() }, ...logins])
      );
      setCurrentUser(adminUser);
      return adminUser;
    }

    if (!isFirebaseConfigured || !auth || !db) {
      const users = JSON.parse(localStorage.getItem("users") || "[]");
      const existingUser =
        users.find((user) => user.email?.toLowerCase() === email.toLowerCase()) || {
          uid: `mock-${Date.now()}`,
          name: email.split("@")[0],
          email,
          phone: "",
          role: adminEmails.includes(email.toLowerCase()) ? "admin" : "user",
        };

      const nextUser = { ...existingUser };
      storeMockSession(nextUser, rememberMe);
      const logins = JSON.parse(localStorage.getItem("loginEvents") || "[]");
      localStorage.setItem(
        "loginEvents",
        JSON.stringify([{ ...nextUser, loggedInAt: new Date().toISOString() }, ...logins])
      );
      setCurrentUser(nextUser);
      return nextUser;
    }

    const credential = await signInWithEmailAndPassword(auth, email, password);
    let profileData = {};

    try {
      const snapshot = await getDoc(doc(db, "users", credential.user.uid));
      profileData = snapshot.exists() ? snapshot.data() : {};
    } catch (error) {
      if (!isOfflineFirestoreError(error)) {
        throw error;
      }
    }

    const role =
      profileData.role || (adminEmails.includes(email.toLowerCase()) ? "admin" : "user");
    const profile = {
      uid: credential.user.uid,
      email: credential.user.email,
      name: credential.user.displayName || profileData.name || email.split("@")[0],
      phone: profileData.phone || "",
      role,
    };

    try {
      await setDoc(
        doc(db, "users", credential.user.uid),
        {
          uid: credential.user.uid,
          name: profile.name,
          email: profile.email,
          phone: profile.phone,
          role,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      if (!isOfflineFirestoreError(error)) {
        throw error;
      }
    }

    setCurrentUser(profile);
    await recordLoginEvent(profile);
    return profile;
  };

  const logout = async () => {
    clearMockSession();

    if (isFirebaseConfigured && auth) {
      await signOut(auth);
    }

    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        loading,
        isAdmin: currentUser?.role === "admin",
        signup,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}

export { HARDCODED_ADMIN };
